/**
 * Settings Page
 * User account settings and preferences
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserCircleIcon,
  BellIcon,
  ShieldCheckIcon,
  LanguageIcon,
  PaintBrushIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/common/SEO';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';

interface SettingsSection {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ReactNode;
}

const SettingsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);

  // Settings State
  const [settings, setSettings] = useState({
    // Notification Settings
    emailNotifications: true,
    claimUpdates: true,
    companyUpdates: false,
    marketingEmails: false,

    // Privacy Settings
    profileVisible: true,
    showEmail: false,
    showPhone: false,

    // Display Settings
    language: 'th',
    theme: 'light',
  });

  // Change Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] as string[] });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Check password strength when new password changes
  useEffect(() => {
    if (passwordData.newPassword) {
      const validation = authService.validatePasswordStrength(passwordData.newPassword);
      setPasswordStrength(validation);
    } else {
      setPasswordStrength({ isValid: false, errors: [] });
    }
  }, [passwordData.newPassword]);

  // Handle setting change
  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle password field change
  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Handle password change submission
  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.currentPassword) {
      toast.error('กรุณากรอกรหัสผ่านปัจจุบัน');
      return;
    }

    if (!passwordData.newPassword) {
      toast.error('กรุณากรอกรหัสผ่านใหม่');
      return;
    }

    if (!passwordData.confirmNewPassword) {
      toast.error('กรุณายืนยันรหัสผ่านใหม่');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      toast.error('รหัสผ่านใหม่ไม่ตรงกัน');
      return;
    }

    if (!passwordStrength.isValid) {
      toast.error('รหัสผ่านไม่ตรงตามเงื่อนไข');
      return;
    }

    setIsChangingPassword(true);
    try {
      await authService.changePassword(passwordData);
      toast.success('เปลี่ยนรหัสผ่านสำเร็จ');

      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      setShowPasswords({
        current: false,
        new: false,
        confirm: false
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน';
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Save settings (PLACEHOLDER - Backend not ready)
  const handleSaveSettings = async () => {
    // Show info toast - feature in development
    toast('ฟีเจอร์กำลังพัฒนา - การตั้งค่าบางส่วนยังไม่พร้อมใช้งาน', {
      duration: 4000,
    });
  };

  // Settings sections
  // NOTE: เมนูอื่นๆ (notifications, privacy, security, display) ถูกลบออกชั่วคราว
  // สามารถเพิ่มกลับได้ในอนาคตโดยดูใน git history
  const sections: SettingsSection[] = [
    {
      id: 'profile',
      title: 'ข้อมูลส่วนตัว',
      description: 'จัดการข้อมูลโปรไฟล์และการตั้งค่าบัญชี',
      icon: UserCircleIcon,
      component: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">ข้อมูลพื้นฐาน</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  อีเมล
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-500 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-neutral-500">
                  ไม่สามารถเปลี่ยนอีเมลได้ กรุณาติดต่อผู้ดูแลระบบ
                </p>
              </div>
              <div className="pt-4">
                <button
                  onClick={() => navigate('/profile')}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  แก้ไขข้อมูลโปรไฟล์
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'security',
      title: 'ความปลอดภัย',
      description: 'จัดการรหัสผ่านและความปลอดภัยบัญชี',
      icon: ShieldCheckIcon,
      component: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center space-x-2">
              <KeyIcon className="w-5 h-5 text-primary-600" />
              <span>เปลี่ยนรหัสผ่าน</span>
            </h3>
            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  รหัสผ่านปัจจุบัน
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="กรอกรหัสผ่านปัจจุบัน"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    {showPasswords.current ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  รหัสผ่านใหม่
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="กรอกรหัสผ่านใหม่"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    {showPasswords.new ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* Password Requirements */}
                {passwordData.newPassword && (
                  <div className="mt-2 space-y-1">
                    {passwordStrength.isValid ? (
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircleIcon className="w-4 h-4" />
                        <span className="text-sm">รหัสผ่านปลอดภัย</span>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {passwordStrength.errors.map((error, index) => (
                          <div key={index} className="flex items-center space-x-2 text-red-600">
                            <XCircleIcon className="w-4 h-4" />
                            <span className="text-sm">{error}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-2 text-xs text-neutral-600">
                  รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร, มีตัวอักษรอย่างน้อย 1 ตัว และมีตัวเลขอย่างน้อย 1 ตัว
                </p>
              </div>

              {/* Confirm New Password */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  ยืนยันรหัสผ่านใหม่
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmNewPassword}
                    onChange={(e) => handlePasswordChange('confirmNewPassword', e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    {showPasswords.confirm ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* Password Match Indicator */}
                {passwordData.confirmNewPassword && (
                  <div className="mt-2">
                    {passwordData.newPassword === passwordData.confirmNewPassword ? (
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircleIcon className="w-4 h-4" />
                        <span className="text-sm">รหัสผ่านตรงกัน</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-red-600">
                        <XCircleIcon className="w-4 h-4" />
                        <span className="text-sm">รหัสผ่านไม่ตรงกัน</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || !passwordStrength.isValid || passwordData.newPassword !== passwordData.confirmNewPassword}
                  className={`px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${
                    isChangingPassword || !passwordStrength.isValid || passwordData.newPassword !== passwordData.confirmNewPassword
                      ? 'bg-neutral-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800'
                  }`}
                >
                  {isChangingPassword ? 'กำลังบันทึก...' : 'เปลี่ยนรหัสผ่าน'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // REMOVED SECTIONS (ย้ายไปเก็บใน git history):
    // - notifications: การแจ้งเตือน
    // - privacy: ความเป็นส่วนตัว
    // - display: การแสดงผล (ภาษา, ธีม)
    //
    // To restore: git log --all -- frontend/src/pages/SettingsPage.tsx
  ];

  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <>
      <SEO
        title="การตั้งค่า | Thai Security Directory"
        description="จัดการการตั้งค่าบัญชีของคุณ"
        url="/settings"
      />

      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-8 sm:py-12">
        <div className="container-wrapper max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Breadcrumbs */}
            <Breadcrumbs />

            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-neutral-900 mb-2">
                การตั้งค่า
              </h1>
              <p className="text-neutral-600">
                จัดการการตั้งค่าบัญชีและความปลอดภัยของคุณ
              </p>
            </div>

            {/* Settings Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar Navigation */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-2 space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                        activeSection === section.id
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      <section.icon className={`w-5 h-5 ${
                        activeSection === section.id ? 'text-primary-600' : 'text-neutral-400'
                      }`} />
                      <span className="font-medium text-sm">{section.title}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content Area */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
                  {sections.map((section) => (
                    activeSection === section.id && (
                      <motion.div
                        key={section.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="mb-6">
                          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                            {section.title}
                          </h2>
                          <p className="text-neutral-600">
                            {section.description}
                          </p>
                        </div>

                        {section.component}

                        {/* Save Button - Only show for sections that need save button (profile และ security มีปุ่มของตัวเอง) */}
                        {section.id !== 'security' && section.id !== 'profile' && (
                          <div className="mt-8 pt-6 border-t border-neutral-200 flex items-center justify-between">
                            <p className="text-sm text-neutral-600">
                              ฟีเจอร์กำลังพัฒนา - ยังไม่สามารถบันทึกการตั้งค่าได้
                            </p>
                            <button
                              onClick={handleSaveSettings}
                              className="flex items-center space-x-2 px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-neutral-400 to-neutral-500 rounded-lg cursor-not-allowed opacity-60"
                              disabled
                            >
                              <ArrowPathIcon className="w-5 h-5" />
                              <span>บันทึก</span>
                            </button>
                          </div>
                        )}
                      </motion.div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
